import React, { useEffect, useState } from 'react';
import { Check, X, AlertCircle, Info, AlertTriangle } from 'lucide-react';
export type ToastType = 'success' | 'error' | 'warning' | 'info';
export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  onClose: (id: string) => void;
  duration?: number;
}
export function Toast({
  id,
  type,
  title,
  message,
  onClose,
  duration = 4000
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    // Trigger enter animation
    requestAnimationFrame(() => setIsVisible(true));
    // Auto dismiss
    const timer = setTimeout(() => {
      handleClose();
    }, duration);
    return () => clearTimeout(timer);
  }, [duration]);
  const handleClose = () => {
    setIsVisible(false);
    // Wait for exit animation to finish before removing from DOM
    setTimeout(() => onClose(id), 300);
  };
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <Check className="w-[18px] h-[18px] text-[#10B981]" data-id="element-3937" />;
      case 'error':
        return <AlertCircle className="w-[18px] h-[18px] text-[#EF4444]" data-id="element-3938" />;
      case 'warning':
        return <AlertTriangle className="w-[18px] h-[18px] text-[#F59E0B]" data-id="element-3939" />;
      case 'info':
        return <Info className="w-[18px] h-[18px] text-[#3B82F6]" data-id="element-3940" />;
    }
  };
  const getBorderColor = () => {
    switch (type) {
      case 'success':
        return 'border-l-[#10B981]';
      case 'error':
        return 'border-l-[#EF4444]';
      case 'warning':
        return 'border-l-[#F59E0B]';
      case 'info':
        return 'border-l-[#3B82F6]';
    }
  };
  return <div className={`
        fixed bottom-6 right-6 z-[1080] min-w-[320px] max-w-[420px]
        bg-white rounded-lg shadow-[0_4px_6px_rgba(0,0,0,0.1)]
        border border-[#E2E8F0] border-l-4 ${getBorderColor()}
        p-4 flex items-start gap-3
        transition-all duration-300 ease-in-out transform
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
      `} role="alert" data-id="element-3941">
      <div className="flex-shrink-0 mt-0.5" data-id="element-3942">{getIcon()}</div>
      <div className="flex-1 min-w-0" data-id="element-3943">
        <h4 className="text-[14px] font-semibold text-[#0F172A] leading-5" data-id="element-3944">
          {title}
        </h4>
        {message && <p className="text-[13px] font-normal text-[#64748B] mt-1 leading-5" data-id="element-3945">
            {message}
          </p>}
      </div>
      <button onClick={handleClose} className="flex-shrink-0 text-[#94A3B8] hover:text-[#0F172A] transition-colors p-1 rounded-md hover:bg-[#F1F5F9]" aria-label="Close notification" data-id="element-3946">
        <X className="w-[18px] h-[18px]" data-id="element-3947" />
      </button>
    </div>;
}
// Toast Container to manage multiple toasts
export function ToastContainer() {
  // This would typically be connected to a global context
  // For this implementation, we'll export a hook or context separately if needed
  return null;
}